import { Flex } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Tooltip } from "@mantine/core";
import { FC, ReactNode, useMemo } from "react";
import { BsThermometerHalf } from "react-icons/bs";
import { IoWaterOutline } from "react-icons/io5";
import { RiSeedlingLine } from "react-icons/ri";
import { WiCloudy, WiCloudyGusts, WiCloudyWindy } from "react-icons/wi";
import { TaskElement } from "../../../hooks/api/karl-farm/useAllTasks";
import {
  ConditionType,
  Level,
  Score,
} from "../../../hooks/api/karl-farm/useScores";
import type { en } from "../../../i18n/en";
import { useTranslation } from "../../../i18n/useTranslation";

type BadgeProps = {
  hue: number;
};
const Badge = styled.div<BadgeProps>`
  padding: 6px 14px;
  border-radius: 100vmax;

  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;

  background: hsl(${({ hue }) => hue} 100% 90%);
  color: hsl(${({ hue }) => hue} 100% 15%);

  user-select: none;
`;

const typeToIcon: Record<ConditionType, ReactNode> = {
  precipitation: <IoWaterOutline size="20px" />,
  temperature: <BsThermometerHalf size="20px" />,
  soil_temperature: <RiSeedlingLine size="20px" />,
  soil_moisture: <RiSeedlingLine size="20px" />,
  clouds: <WiCloudy size="20px" />,
  wind: <WiCloudyWindy size="20px" />,
  gusts: <WiCloudyGusts size="20px" />,
};

type ConditionBadgeProps = {
  task: TaskElement;
  type: ConditionType;
  level: Level;
  score?: Score;
};
export const ConditionBadge: FC<ConditionBadgeProps> = ({
  task,
  type: conditionType,
  level,
  score,
}) => {
  const { t } = useTranslation();

  type ConditionLevelProps = {
    hue: number;
    label: string;
  };
  const levelToConditionLevelProps: Record<Level, ConditionLevelProps> = {
    optimal: {
      hue: 120,
      label: t("component.ConditionBadge.conditionLevel.optimal"),
    },
    good: {
      hue: 90,
      label: t("component.ConditionBadge.conditionLevel.good"),
    },
    moderate: {
      hue: 60,
      label: t("component.ConditionBadge.conditionLevel.moderate"),
    },
    poor: {
      hue: 30,
      label: t("component.ConditionBadge.conditionLevel.poor"),
    },
    critical: {
      hue: 0,
      label: t("component.ConditionBadge.conditionLevel.critical"),
    },
  };

  const typeToTypeLabel: Record<ConditionType, string> = {
    precipitation: t("component.ConditionBadge.typeLabel.precipitation"),
    temperature: t("component.ConditionBadge.typeLabel.temperature"),
    soil_temperature: t("component.ConditionBadge.typeLabel.soilTemperature"),
    soil_moisture: t("component.ConditionBadge.typeLabel.soilMoisture"),
    clouds: t("component.ConditionBadge.typeLabel.clouds"),
    wind: t("component.ConditionBadge.typeLabel.wind"),
    gusts: t("component.ConditionBadge.typeLabel.gusts"),
  };

  const { hue, label: conditionLabel } = levelToConditionLevelProps[level];
  const icon = typeToIcon[conditionType];
  const typeLabel = typeToTypeLabel[conditionType];

  const descriptionTranslation = useMemo(() => {
    if (score == null) return "Description unavailable.";

    const patchedConditionType =
      conditionType === "gusts" ? "wind" : conditionType;

    const unsafeTask = task.attributes.task;
    const patchedTask = unsafeTask === "seeding" ? "sowing" : unsafeTask;

    const highLow = score.description[conditionType]?.Detail ? "high" : "low";

    const translationKey =
      patchedTask === "spraying"
        ? (`conditions.descriptions.${patchedTask}.${task.attributes.drop_size}.${patchedConditionType}.${highLow}.${level}` as keyof typeof en)
        : patchedTask === "spreading"
        ? (`conditions.descriptions.${patchedTask}.solid_fertilizer.${patchedConditionType}.${highLow}.${level}` as keyof typeof en)
        : (`conditions.descriptions.${patchedTask}.${patchedConditionType}.${highLow}.${level}` as keyof typeof en);

    const translation = t(translationKey);

    return translation;
  }, [conditionType, level, score, task, t]);

  return (
    <Tooltip
      label={descriptionTranslation}
      disabled={!descriptionTranslation}
      withArrow
    >
      <Badge hue={hue}>
        <Flex alignItems="center" gap={"6px"}>
          {icon}
          <div>{typeLabel}</div>
        </Flex>
        <div>{conditionLabel}</div>
      </Badge>
    </Tooltip>
  );
};
